/* Quadruped template ("Vierbeiner"): primitives on a four-legged skeleton -> rig for free.
   Mirrors the serpent branch of compileRecipe in ../voxel.js: returns { prims, parts, mouth, r }.

   Recipe shape:
     { name, template: 'quadruped',
       build: { length, height, legLength, neckLength, bulk, headScale, jaw?, tail?, headShape?, neckAngle? },
       colors: { body, belly?, head?, neck?, jaw?, legs?, hooves? (alias feet), tail? },
       mount?: { on: 'body' | 'head', at: [x, y, z] },   // optional weapon anchor (riders); default = mouth
       add: [{ on: 'body'|'head'|'neck'|'jaw'|'legs'|'legFL'|'legFR'|'legBL'|'legBR'|'tail', shape, at, size, color, mirror, rot, glow, stripes, checker }] }

   build (all optional):
     length     body length in metres (front-back), default 3
     height     back height in metres (ground -> top of back), default 2
     legLength  fraction of height that is free leg (0.2..0.8), default 0.5; values > 1 are read as metres
     neckLength neck length as fraction of body length (0 = head sits on the chest), default 0.2; values > 1 are metres
     bulk       body width multiplier, default 1 (recipeModel's build.width multiplies it too)
     headScale  head size multiplier, default 1
     jaw        jaw size multiplier, default 1
     tail       tail length multiplier (0 = no tail prim), default 1
     headShape  'box' | 'ellipsoid', default 'box'
     neckAngle  neck elevation in degrees above horizontal, default 50

   Coordinates: metres, front is +z, ground is y = 0, legs reach the ground. `at`/`size` are normalized to the
   half extents of the target part frame (same convention as the humanoid template). The tail frame is the
   bounding box of the drooping tail, so the tail tip is near at: [0, -0.85, -0.85].
   'legs' applies to all four legs; left legs are mirrored so +x always points outward.
   'neck' uses the unrotated neck box (falls back to the head frame when there is no neck); it belongs to the head part.

   Note: this module only uses its imports inside functions, so voxel.js may import it back (ES module cycle is safe). */
import { KEY, ROLE, VoxModel, rPrim } from '../voxel.js';
import { fineFactor, headPrims, headRegion, rasterize } from '../heads.js';
import { faceModeOf } from '../faceMode.js';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const DEG = Math.PI / 180;

/* Frames (centre + half extents, metres) and pivots of the skeleton. Exported for previews and tooling. */
export function quadrupedFrames(build = {}) {
  const L = build.length ?? 3, H = build.height ?? 2;
  // legLength / neckLength: values <= 1 are fractions (of height / body length), larger values are metres
  const legRaw = build.legLength ?? 0.5, neckRaw = build.neckLength ?? 0.2;
  const legF = clamp(legRaw > 1 ? legRaw / H : legRaw, 0.2, 0.8), neckF = Math.max(0, neckRaw > 1 ? neckRaw / L : neckRaw);
  const bulk = (build.bulk ?? 1) * (build.width ?? 1), hs = build.headScale ?? 1, js = build.jaw ?? 1, tailF = build.tail ?? 1;
  const legH = H * legF;
  const bhy = (H - legH) / 2, by = legH + bhy, bhz = L / 2;
  const bhx = Math.min(bhy * 0.9 * bulk, L * 0.38);
  const body = { c: [0, by, 0], h: [bhx, bhy, bhz] };
  // legs: thick posts under the four corners of the body, overlapping into it
  const lw = Math.max(0.05, bhx * 0.3);
  const lx = Math.max(lw, bhx - lw * 1.1), lz = bhz - Math.max(lw * 1.3, bhz * 0.22);
  const ov = bhy * 0.6, legHY = (legH + ov) / 2;
  const leg = (sx, sz) => ({ c: [sx * lx, legHY, sz * lz], h: [lw, legHY, lw * 1.05] });
  const legs = { legFL: leg(-1, 1), legFR: leg(1, 1), legBL: leg(-1, -1), legBR: leg(1, -1) };
  const legPivot = (sx, sz) => [sx * lx, legH + ov * 0.5, sz * lz];
  // neck rises from the front top of the body, head sits at its end
  const nb = [0, by + bhy * 0.35, bhz * 0.75];
  const nLen = neckF * L, na = (build.neckAngle ?? 50) * DEG;
  const ne = [0, nb[1] + nLen * Math.sin(na), nb[2] + nLen * Math.cos(na)];
  const nr = bhy * 0.42;
  const neck = nLen > 0.05 ? { c: [0, (nb[1] + ne[1]) / 2, (nb[2] + ne[2]) / 2], h: [bhx * 0.5, nLen / 2 + nr * 0.6, nr], rot: [90 - na / DEG, 0, 0] } : null;
  const hh = [bhx * 0.62 * hs, bhy * 0.6 * hs, bhy * 0.75 * hs];
  const head = { c: [0, ne[1] + hh[1] * 0.2, ne[2] + hh[2] * 0.55], h: hh };
  const jaw = { c: [0, head.c[1] - hh[1] * 0.85, head.c[2] + hh[2] * 0.2], h: [hh[0] * 0.8 * js, hh[1] * 0.28 * js, hh[2] * 0.85 * js] };
  const jawPivot = [0, head.c[1] - hh[1] * 0.6, head.c[2] - hh[2] * 0.6];
  // tail droops 35 degrees behind the rump
  const tl = L * 0.3 * tailF, tr = Math.max(0.04, bhx * 0.12), td = 35 * DEG;
  const tb = [0, by + bhy * 0.45, -bhz * 0.92];
  const tc = [0, tb[1] - Math.sin(td) * tl / 2, tb[2] - Math.cos(td) * tl / 2];
  const tailCore = { c: tc, h: [tr, tr, tl / 2], rot: [-35, 0, 0] };
  const tail = { c: tc, h: [tr, Math.sin(td) * tl / 2 + tr, Math.cos(td) * tl / 2 + tr] };
  return {
    L, H, legH, body, legs, neck, head, jaw, tail, tailCore, hasTail: tl > 0.05,
    pivots: { head: nb, jaw: jawPivot, tail: tb, legFL: legPivot(-1, 1), legFR: legPivot(1, 1), legBL: legPivot(-1, -1), legBR: legPivot(1, -1) },
  };
}

/* Voxel size used when rasterizing a quadruped: humanoid-fine for small beasts, coarser for giants. */
export function quadrupedVoxelSize(build = {}) {
  return clamp(Math.max(build.length ?? 3, build.height ?? 2) / 38, 0.066, 0.16);
}

// part indices (fixed layout, 8 parts)
const PI = { body: 0, head: 1, jaw: 2, legFL: 3, legFR: 4, legBL: 5, legBR: 6, tail: 7 };

export function compileQuadruped(rec, t, build) {
  const b = build || rec.build || {};
  const F = quadrupedFrames(b);
  const col = rec.colors || {};
  const prims = [];
  const parts = [
    { role: ROLE.ROOT, pivot: [0, 0, 0] },
    { role: ROLE.HEAD, pivot: F.pivots.head },
    { role: ROLE.JAW, pivot: F.pivots.jaw },
    { role: ROLE.LEGL, pivot: F.pivots.legFL },
    { role: ROLE.LEGR, pivot: F.pivots.legFR },
    { role: ROLE.LEGR, pivot: F.pivots.legBL },
    { role: ROLE.LEGL, pivot: F.pivots.legBR },
    { role: ROLE.TAIL, pivot: F.pivots.tail },
  ];
  const cBody = col.body || '#888888', cLegs = col.legs || cBody, cHead = col.head || cBody;
  // body: rounded ellipsoid + boxy core for the chunky voxel look, optional belly
  prims.push(rPrim(F.body, { shape: 'ellipsoid', color: cBody }, PI.body, false));
  prims.push(rPrim(F.body, { shape: 'box', size: [0.82, 0.78, 0.86], color: cBody }, PI.body, false));
  if (col.belly) prims.push(rPrim(F.body, { shape: 'ellipsoid', at: [0, -0.3, 0], size: [0.9, 0.75, 0.88], color: col.belly }, PI.body, false));
  // legs + hooves
  for (const k of ['legFL', 'legFR', 'legBL', 'legBR']) {
    const f = F.legs[k];
    prims.push(rPrim(f, { shape: 'box', color: cLegs }, PI[k], false));
    const hoof = clamp(Math.max(0.03, F.legH * 0.055) / f.h[1], 0.06, 0.3);
    prims.push(rPrim(f, { shape: 'box', at: [0, -1 + hoof, 0.08], size: [1.14, hoof, 1.18], color: col.hooves || col.feet || cLegs }, PI[k], false));
  }
  // neck (belongs to the head part, so it nods with it), head, jaw
  if (F.neck) prims.push(rPrim({ c: F.neck.c, h: F.neck.h }, { shape: 'cylinder', rot: F.neck.rot, color: col.neck || cBody }, PI.head, false));
  prims.push(Object.assign(rPrim(F.head, { shape: b.headShape || 'box', color: cHead }, PI.head, false), { hf: true, face: true }));
  prims.push(Object.assign(rPrim(F.jaw, { shape: 'box', color: col.jaw || cHead }, PI.jaw, false), { hf: true, face: true }));
  if (F.hasTail) prims.push(rPrim(F.tailCore, { shape: 'ellipsoid', rot: F.tailCore.rot, color: col.tail || cBody }, PI.tail, false));
  // decorations
  const LEGS = ['legFR', 'legFL', 'legBR', 'legBL']; // right legs unmirrored, left legs mirrored
  const neckFrame = F.neck ? { c: F.neck.c, h: [F.neck.h[0], (F.neck.h[1] + F.neck.h[2]) * 0.75, (F.neck.h[1] + F.neck.h[2]) * 0.75] } : F.head;
  const frameOf = k => k === 'body' ? F.body : k === 'head' ? F.head : k === 'neck' ? neckFrame : k === 'jaw' ? F.jaw : k === 'tail' ? F.tail : F.legs[k];
  const partOf = k => k === 'neck' ? PI.head : PI[k];
  // face, hair and headgear (../faces.js, ../hair.js); legacy face boxes are replaced by the painted face
  const hd = faceModeOf(rec) === 'aus' ? { before: [], after: [], add: rec.add || [] } : headPrims(rec, { kind: 'quadruped', head: F.head, jaw: F.jaw, headPart: PI.head, jawPart: PI.jaw }, rPrim);
  prims.push(...hd.before);
  for (const a of hd.add) {
    if (a.on === 'legs') {
      LEGS.forEach(k => {
        const left = k === 'legFL' || k === 'legBL';
        prims.push(rPrim(F.legs[k], a, PI[k], left));
      });
      continue;
    }
    const f = frameOf(a.on), pi = partOf(a.on);
    if (!f || pi === undefined) continue;
    const hf = a.on === 'head' || a.on === 'jaw' ? { hf: true } : {};
    prims.push(Object.assign(rPrim(f, a, pi, false), hf));
    if (a.mirror) prims.push(Object.assign(rPrim(f, a, pi, true), hf));
  }
  prims.push(...hd.after);
  // weapon anchor: the mouth by default; riders can move it onto the body (mouth.part tells which part)
  let mouth = { y: F.jaw.c[1] + F.jaw.h[1], z: F.head.c[2] + F.head.h[2], x: 0, part: PI.head };
  if (rec.mount && (rec.mount.on === 'body' || rec.mount.on === 'head')) {
    const f = rec.mount.on === 'body' ? F.body : F.head, at = rec.mount.at || [0, 0, 1];
    mouth = { x: f.c[0] + at[0] * f.h[0], y: f.c[1] + at[1] * f.h[1], z: f.c[2] + at[2] * f.h[2], part: PI[rec.mount.on] };
  }
  return { prims, parts, mouth, r: F.body.h[0], head: F.head };
}

/* Rasterize compiled primitives into a VoxModel exactly like recipeModel in voxel.js does. */
export function rasterizePrims(m, prims, vs) {
  const hmin = vs * 0.55;
  for (const p of prims) {
    if (p.op) continue; // paint operations (face, hair) need heads.js rasterize()
    const h = p.h.map(v => Math.max(v, hmin));
    const e = p.inv ? [Math.hypot(...h), Math.hypot(...h), Math.hypot(...h)] : h;
    const lo = [0, 1, 2].map(a => Math.floor((p.c[a] - e[a]) / vs)), hi = [0, 1, 2].map(a => Math.floor((p.c[a] + e[a]) / vs));
    lo[1] = Math.max(lo[1], 0);
    const M = p.inv;
    for (let z = lo[2]; z <= hi[2]; z++) for (let y = lo[1]; y <= hi[1]; y++) for (let x = lo[0]; x <= hi[0]; x++) {
      let qx = (x + 0.5) * vs - p.c[0], qy = (y + 0.5) * vs - p.c[1], qz = (z + 0.5) * vs - p.c[2];
      if (M) { const tx = M[0] * qx + M[4] * qy + M[8] * qz, ty = M[1] * qx + M[5] * qy + M[9] * qz, tz = M[2] * qx + M[6] * qy + M[10] * qz; qx = tx; qy = ty; qz = tz; }
      const ax = Math.abs(qx) / h[0], ay = Math.abs(qy) / h[1], az = Math.abs(qz) / h[2];
      let inside;
      if (p.shape === 'ellipsoid') inside = ax * ax + ay * ay + az * az <= 1;
      else if (p.shape === 'cylinder') inside = p.axis === 'x' ? ax <= 1 && ay * ay + az * az <= 1 : p.axis === 'z' ? az <= 1 && ax * ax + ay * ay <= 1 : ay <= 1 && ax * ax + az * az <= 1;
      else if (p.shape === 'cone') { const rf = 1 - ((qy / h[1] + 1) / 2) * 0.9; inside = ay <= 1 && ax * ax + az * az <= rf * rf; }
      else inside = ax <= 1 && ay <= 1 && az <= 1;
      if (!inside) continue;
      let c = p.color;
      if (p.stripes) { const wv = [(x + 0.5) * vs, (y + 0.5) * vs, (z + 0.5) * vs][p.stripes.axis]; if (Math.floor(wv / p.stripes.period) & 1) c = p.stripes.color; }
      else if (p.checker !== null && ((x + y + z) & 1)) c = p.checker;
      m.map.set(KEY(x, y, z), { c, p: p.part, e: p.glow ? 1 : 0 });
    }
  }
  return m;
}

/* Full model for a quadruped recipe (what recipeModel should return for template 'quadruped'). */
export function quadrupedModel(rec, o = {}) {
  const t = o.t ?? 0.4;
  const b = { ...(rec.build || {}) };
  b.width = (b.width || 1) * (o.width || 1); b.headScale = (b.headScale || 1) * (o.head || 1);
  const comp = compileQuadruped(rec, t, b);
  // detail 2 = half voxel size (close-up LOD); giant beasts already use coarse voxels and would explode to 150k+, so they keep detail 1
  const k = o.detail > 1 && quadrupedVoxelSize(b) < 0.09 ? 2 : 1;
  const vs = quadrupedVoxelSize(b) / k;
  const m = new VoxModel(vs);
  m.detail = k;
  // close-up LOD (also for giants, which keep their coarse body): the head region gets a finer voxel layer (../heads.js)
  let fine = null;
  if (o.paint === false) fine = { f: 1, region: null, paint: false };
  else if (o.detail > 1 && o.fineHead !== false && faceModeOf(rec) === 'fein') {
    const f = fineFactor(comp.head.h[0], vs), H = comp.head;
    if (f > 1) fine = { f, region: headRegion(comp.prims, vs, { lo: [H.c[0] - H.h[0] * 2.6, H.c[1] - H.h[1] * 4.2, H.c[2] - H.h[2] * 1.6], hi: [H.c[0] + H.h[0] * 2.6, H.c[1] + H.h[1] * 3.2, H.c[2] + H.h[2] * 3.4] }), parts: [PI.head, PI.jaw] };
  }
  rasterize(m, comp.prims, vs, fine);
  m.parts = comp.parts.map(pp => ({ role: pp.role, pivot: pp.pivot.map(v => v / vs), base: pp.base || 0 }));
  m.kind = 'quadruped'; m.atkStyle = 1;
  const M = comp.mouth;
  m.mountMelee = m.mountRanged = { part: M.part, a: [Math.round(M.x / vs) - k, Math.round(M.y / vs), Math.floor(M.z / vs) + k], f: [0, 0, 1], u: [0, 1, 0], s: [1, 0, 0] };
  return m;
}
